/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime } from 'luxon';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { Route, Routes } from 'react-router';

import { Box, CircularProgress } from '@mui/material';

import { HURoute } from '../../components/HURoute/HURoute';
import { BasicRoutes } from '../../constants/basicRoutes';
import { AboutPageLayout } from '../../layouts/AboutPageLayout/AboutPageLayout';
import { LoginLayout } from '../../layouts/LoginLayout/LoginLayout';
import { MainLayout } from '../../layouts/MainLayout/MainLayout';
import { NotFoundLayout } from '../../layouts/NotFoundLayout/NotFoundLayout';
import { ProfileLayout } from '../../layouts/ProfileLayout/ProfileLayout';
import { SetupLayout } from '../../layouts/SetupLayout/SetupLayout';
import { SimpleLayout } from '../../layouts/SimpleLayout/SimpleLayout';
import { AboutPage } from '../../pages/AboutPage/AboutPage';
import { AccountProfilePage } from '../../pages/AccontProfilePage/AccountProfilePage';
import { ClusterPage } from '../../pages/ClusterPage/ClusterPage';
import { ConfirmationPage } from '../../pages/ConfirmationPage/ConfirmationPage';
import { EmailVerificationPage } from '../../pages/EmailVerificationPage/EmailVerificationPage';
import { ForgotPage } from '../../pages/ForgotPage/ForgotPage';
import { HomePage } from '../../pages/HomePage/HomePage';
import { ImprintPage } from '../../pages/ImprintPage/ImprintPage';
import { LoginPage } from '../../pages/LoginPage/LoginPage';
import { ManageOrganizationPage } from '../../pages/ManageOrganizationPage/ManageOrganizationPage';
import { ManageProjectPage } from '../../pages/ManageProjectPage/ManageProjectPage';
import { NotFoundPage } from '../../pages/NotFoundPage/NotFoundPage';
import { OrganizationPage } from '../../pages/OrganizationPage/OrganizationPage';
import { OrganizationsPage } from '../../pages/OrganizationsPage/OrganizationsPage';
import { ProjectPage } from '../../pages/ProjectPage/ProjectPage';
import { RecoveryPasswordPage } from '../../pages/RecoveryPasswordPage/RecoveryPasswordPage';
import { SetupPage } from '../../pages/SetupPage/SetupPage';
import { SignUpPage } from '../../pages/SignUpPage/SignUpPage';
import { SsoCallbackPage } from '../../pages/SsoCallbackPage/SsoCallbackPage';
import { SupportPage } from '../../pages/SupportPage/SupportPage';
import { RootState } from '../../store';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

const GeneralSectionRoutesComponent = memo<PropsFromRedux>(props => {
  const {
    auth: { isAuthCheckCompleted },
  } = props;
  const { i18n } = useTranslation();

  useEffect(() => {
    DateTime.now().setLocale(i18n.language);
  }, [i18n.language]);

  if (!isAuthCheckCompleted) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
        <CircularProgress color="primary" size={60} />
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        path={BasicRoutes.LOGIN}
        element={
          <HURoute guestRoute>
            <LoginLayout>
              <LoginPage />
            </LoginLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.SSO_CALLBACK}
        element={
          <HURoute guestRoute>
            <SsoCallbackPage />
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.SETUP}
        element={
          <HURoute>
            <SetupLayout>
              <SetupPage />
            </SetupLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.SIGN_UP}
        element={
          <HURoute guestRoute>
            <LoginLayout>
              <SignUpPage />
            </LoginLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.HOME}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <HomePage />
            </MainLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.FORGOT_PASSWORD}
        element={
          <HURoute guestRoute>
            <LoginLayout>
              <ForgotPage />
            </LoginLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.EMAIL_VERIFICATION}
        element={
          <HURoute>
            <SimpleLayout>
              <EmailVerificationPage />
            </SimpleLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.RECOVERY_PASSWORD}
        element={
          <HURoute>
            <SimpleLayout>
              <RecoveryPasswordPage />
            </SimpleLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.SUPPORT}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <SupportPage />
            </MainLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.IMPRINT}
        element={
          <HURoute>
            <ImprintPage />
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.ORGANIZATION_PAGE}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <OrganizationPage />
            </MainLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.MANAGE_ORGANIZATION}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <ManageOrganizationPage />
            </MainLayout>
          </HURoute>
        }
      >
        <Route
          path="*"
          element={
            <HURoute privateRoute>
              <MainLayout>
                <ManageOrganizationPage />
              </MainLayout>
            </HURoute>
          }
        />
      </Route>
      <Route
        path={BasicRoutes.PROJECT_PAGE}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <ProjectPage />
            </MainLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.MANAGE_PROJECT}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <ManageProjectPage />
            </MainLayout>
          </HURoute>
        }
      >
        <Route
          path="*"
          element={
            <HURoute privateRoute>
              <MainLayout>
                <ManageProjectPage />
              </MainLayout>
            </HURoute>
          }
        />
      </Route>
      <Route
        path={BasicRoutes.CLUSTER_PAGE}
        element={
          <HURoute privateRoute>
            <MainLayout>
              <ClusterPage />
            </MainLayout>
          </HURoute>
        }
      >
        <Route
          path="*"
          element={
            <HURoute privateRoute>
              <MainLayout>
                <ClusterPage />
              </MainLayout>
            </HURoute>
          }
        />
      </Route>
      <Route
        path={BasicRoutes.PROFILE}
        element={
          <HURoute privateRoute>
            <ProfileLayout>
              <AccountProfilePage />
            </ProfileLayout>
          </HURoute>
        }
      >
        <Route
          path="*"
          element={
            <HURoute privateRoute>
              <ProfileLayout>
                <AccountProfilePage />
              </ProfileLayout>
            </HURoute>
          }
        />
      </Route>
      <Route
        path={BasicRoutes.ORGANIZATIONS}
        element={
          <HURoute privateRoute>
            <ProfileLayout>
              <OrganizationsPage />
            </ProfileLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.CONFIRM_PASSWORD_RESET}
        element={
          <HURoute>
            <SimpleLayout>
              <ConfirmationPage />
            </SimpleLayout>
          </HURoute>
        }
      />
      <Route
        path={BasicRoutes.ABOUT}
        element={
          <HURoute>
            <AboutPageLayout>
              <AboutPage />
            </AboutPageLayout>
          </HURoute>
        }
      />
      <Route
        path="*"
        element={
          <HURoute privateRoute>
            <NotFoundLayout>
              <NotFoundPage />
            </NotFoundLayout>
          </HURoute>
        }
      />
    </Routes>
  );
});

export const GeneralSectionRoutes = connector(GeneralSectionRoutesComponent);
